import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as api from '../../../api'
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {FD100MultiSelectOptions} from '../../Utils/Listings';
import FabFrIndicator from '../../Atoms/FabFrIndicator';
import { getEventDesignerDisplayName, getEventDesignerProfileLink } from '../../../Components/AdminPanels/utils/eventDesignerHelpers';
import BoldBlue from '../../Atoms/BoldBlue';
import H2 from '../../Atoms/H2';
import Spinner from '../../Atoms/Spinner';

const RatingLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-top: 1px solid #ccc;
    font-size: 13px;
    transition: background-color 0.3s ease;

    &.selected {
        background-color: #d4edda;
        border-color: #c3e6cb;
        
        &:hover {
            background-color: #c3e6cb;
        }
    }

    > .projinfo {
        width: 70%;
        flex-grow: 1;

        a{
            color: black;
            text-decoration: none;
            cursor: pointer;
            &:hover{
                color: blue;
            }
            &:visited{
                color: black;
            }
        }
    }

    > label {
        display: flex;
        align-items: center;
        margin: 0 20px;

        > span {
            margin-right: 10px;
            font-weight: 600;
        }

        > input {
            margin: 0;
            width: 20px;
            height: 20px;
            cursor: pointer;
        }
    }
`

const StatsContainer = styled.div`
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .stats-item {
        text-align: center;
        
        .number {
            font-size: 2em;
            font-weight: bold;
            color: #0000ff;
        }
        
        .label {
            font-size: 0.9em;
            color: #6c757d;
            margin-top: 5px;
        }
    }
    
    .progress-container {
        flex: 1;
        margin: 0 30px;
        
        .progress-bar {
            width: 100%;
            height: 20px;
            background: #e9ecef;
            border-radius: 10px;
            overflow: hidden;
            
            .progress-fill {
                height: 100%;
                background: #0000ff;
                transition: width 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 0.8em;
                font-weight: bold;
            }
        }
        
        .progress-label {
            text-align: center;
            margin-top: 8px;
            font-size: 0.9em;
            color: #6c757d;
        }
    }
`

const SaveNotification = styled.div`
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: ${props => props.status === 'loading' ? '#0000ff' : props.status === 'saved' ? '#28a745' : '#dc3545'};
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    transition: all 0.3s ease;
    opacity: ${props => props.show ? 1 : 0};
    transform: translateX(-50%) translateY(${props => props.show ? '0' : '20px'});
    
    @keyframes fadeOut {
        0% { opacity: 1; transform: translateX(-50%) translateY(0); }
        100% { opacity: 0; transform: translateX(-50%) translateY(20px); }
    }
    
    .spinner {
        margin-right: 10px;
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255,255,255,0.3);
        border-top: 2px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    .checkmark {
        margin-right: 8px;
        font-size: 18px;
    }
`

const CategoryHeader = styled.div`
    color: white;
    padding: 8px 12px;
    margin: 15px 0 0 0;
    border-radius: 4px;
    font-weight: 500;
    font-size: 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    
    &.objet-category {
        background: linear-gradient(135deg,rgb(228, 209, 0),rgb(255, 243, 8));
    }
    
    &.espace-category {
        background: linear-gradient(135deg, #fd7e14, #e55100);
    }
`

const InfoBox = styled.div`
    background: #fff3cd;
    border: 1px solid #ffeeba;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
    color: #856404;
    
    h3 {
        margin-top: 0;
        margin-bottom: 10px;
        font-size: 16px;
    }
    
    ul {
        margin: 5px 0;
        padding-left: 20px;
        
        li {
            margin: 5px 0;
        }
    }
`

const FD100_2025JuryDepartage = (props) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const [event, setEvent] = React.useState({});
    const [allProjects, setAllProjects] = React.useState([]);
    const [ballotageProjects, setBallotageProjects] = React.useState([]);
    const [allRatings, setAllRatings] = React.useState([]);
    const [userRatings, setUserRatings] = React.useState([]);
    const [dataStatus, setDataStatus] = React.useState("loading");
    const [showSaveNotification, setShowSaveNotification] = React.useState(false);
    const [hasUserInteracted, setHasUserInteracted] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    // Get event
    useEffect(() => {
        if (props.user?.eventId) {
            api.getEventById({ id: props.user.eventId }).then((response) => {
                setEvent(response.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [props.user?.eventId])

    // Get all participations and calculate ballotage projects
    useEffect(() => {
        if (event._id) {
            setLoading(true);
            
            // Get all projects
            api.getParticipatingUsersWithData({ eventId: event._id }).then((response) => {
                let _allProjects = []
                response.data.forEach(user => {
                    if (user.participation.data.projets) {
                        user.participation.data.projets.forEach(project_id => {
                            const projectObject = user.company.projects.find(p => p._id === project_id);
                            if (!projectObject) {
                                return;
                            }

                            if(projectObject.status === "validé"){
                                projectObject.participationId = user.participation._id;
                                _allProjects.push({ projet: projectObject, user: user })
                            }
                        })
                    }
                })
                setAllProjects(_allProjects);
                
                // Get all jury ratings to calculate scores
                api.getAllJuriesForEvent({ eventId: event._id }).then((response) => {
                    let _allJuries = response.data.sort((a,b) => a.last_name.localeCompare(b.last_name));
                    let _ratings = [];
                    _allJuries.forEach(jury => {
                        if (jury.juryNotes) {
                            jury.juryNotes.forEach(rating => {
                                rating.jury = jury;
                                let _projetIndex = _ratings.findIndex(r => r.id === rating.participationId + rating.projetId);
                                if (_projetIndex === -1) {
                                    _ratings.push({id : rating.participationId + rating.projetId, ratings : [{rating}]});
                                } else {
                                    _ratings[_projetIndex].ratings.push({rating});
                                }
                            });
                        }
                    });
                    setAllRatings(_ratings);
                    
                    // Calculate ballotage projects
                    const _ballotageProjects = _allProjects.filter(project => {
                        const projectRatings = _ratings.find(r => r.id === project.projet.participationId + project.projet._id);
                        if (!projectRatings) return false;
                        
                        const totalVotes = projectRatings.ratings.reduce((sum, r) => sum + (r.rating.stars || 0), 0);
                        const totalMentions = projectRatings.ratings.reduce((sum, r) => sum + (r.rating.Coup_de_coeur === "checked" ? 1 : 0), 0);
                        const hasFrenchFab = project.projet.fabrication_fr === "oui";
                        
                        return totalVotes === 13 && totalMentions === 0 && hasFrenchFab;
                    });
                    
                    setBallotageProjects(_ballotageProjects);
                    setDataStatus("saved");
                    setLoading(false);
                }).catch((error) => {
                    console.log(error);
                    setLoading(false);
                });
            }).catch((error) => {
                console.log(error);
                setLoading(false);
            });
        }
    }, [event._id])

    // Get user's ratings
    React.useEffect(() => {
        getUserRatings();
    },[]);

    async function getUserRatings() {
        api.GetCurrentUser().then((response) => {
            setUserRatings(response.data.juryNotes || []);
        }).catch((error) => {
            console.log(error);
            navigate('/');
        });
    }

    // Save ratings whenever they change
    React.useEffect(() => {
        if(dataStatus === "unsaved"){
            setDataStatus("loading");
            if(hasUserInteracted) {
                setShowSaveNotification(true);
            }
            api.setRatings(props.user._id, userRatings).then(() => {
                setDataStatus("saved");
                if(hasUserInteracted) {
                    setTimeout(() => {
                        setShowSaveNotification(false);
                    }, 3000);
                }
            });
        }
    },[userRatings, dataStatus, hasUserInteracted, props.user._id]);

    async function setDepartagePoint(projet, e){
        setHasUserInteracted(true);
        let _ratings = [...userRatings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);
        
        if(_ratingIndex !== -1){
            if (e.target.checked) {
                _ratings[_ratingIndex].jury2025_departage_point = "1";
            } else {
                delete _ratings[_ratingIndex].jury2025_departage_point;
            }
        } else {
            if (e.target.checked) {
                _ratings.push({
                    participationId: projet.projet.participationId, 
                    projetId: projet.projet._id, 
                    jury2025_departage_point: "1"
                });
            }
        }
        setUserRatings(_ratings);
        setDataStatus("unsaved");
    }

    const typologieEspaceFD100 = FD100MultiSelectOptions.tyoplogieEspaceFD100("en");
    const typologieObjetFD100 = FD100MultiSelectOptions.typologieObjetFD100("en");

    const FDCategories = [
        ...typologieEspaceFD100,
        ...typologieObjetFD100,
    ].filter((cat, index, self) => self.findIndex((t) => t.value === cat.value) === index)

    // Helper function to determine if a category is OBJET or ESPACE
    const getCategoryType = (categoryValue) => {
        const isObjetCategory = typologieObjetFD100.some(cat => cat.value === categoryValue);
        return isObjetCategory ? 'objet' : 'espace';
    };

    // Category display mapping for visual merging
    const categoryDisplayMapping = {
        // ESPACE categories
        'hotel': { displayName: 'Hotel', order: 1, type: 'espace' },
        'residence': { displayName: 'Private Residence', order: 2, type: 'espace' },
        'retail': { displayName: 'Retail/Office', order: 4, type: 'espace' },
        'office': { displayName: 'Retail/Office', order: 4, type: 'espace' },
        'culturel': { displayName: 'Cultural Space / Installation / Scenography', order: 5, type: 'espace' },
        'install': { displayName: 'Cultural Space / Installation / Scenography', order: 5, type: 'espace' },
        'sceno': { displayName: 'Cultural Space / Installation / Scenography', order: 5, type: 'espace' },
        'archi': { displayName: 'Architecture', order: 6, type: 'espace' },
        
        // OBJET categories
        'assise': { displayName: 'Seatings', order: 7, type: 'objet' },
        'table': { displayName: 'Table', order: 8, type: 'objet' },
        'luminaire': { displayName: 'Lighting', order: 9, type: 'objet' },
        'collection': { displayName: 'Collection', order: 10, type: 'objet' },
        'armoire': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'bibliotheque': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'etagere': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'buffet': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'console': { displayName: 'Storage Furniture', order: 11, type: 'objet' },
        'miroir': { displayName: 'Accessories', order: 12, type: 'objet' },
        'paravent': { displayName: 'Accessories', order: 12, type: 'objet' },
        'tapis': { displayName: 'Accessories', order: 12, type: 'objet' },
        'accesoire': { displayName: 'Accessories', order: 12, type: 'objet' },
        
        // Other categories (keep as is)
        'restaurant': { displayName: 'Restaurant', order: 3, type: 'espace' },
        'urbain': { displayName: 'Urban space', order: 13, type: 'espace' },
        'commode': { displayName: 'Commode', order: 14, type: 'objet' },
        'portemanteau': { displayName: 'Portemanteau', order: 15, type: 'objet' },
        'autre': { displayName: 'Other', order: 16, type: 'any' }
    };

    // Get display information for a category
    const getCategoryDisplayInfo = (categoryValue) => {
        return categoryDisplayMapping[categoryValue] || {
            displayName: categoryValue,
            order: 999,
            type: getCategoryType(categoryValue)
        };
    };

    // Group projects by display categories
    const getDisplayGroups = () => {
        const displayGroups = {};
        
        ballotageProjects.forEach(project => {
            const displayInfo = getCategoryDisplayInfo(project.projet.typologie);
            const displayName = displayInfo.displayName;
            
            if (!displayGroups[displayName]) {
                displayGroups[displayName] = {
                    displayName,
                    order: displayInfo.order,
                    type: displayInfo.type,
                    categoryValues: new Set(),
                    projects: []
                };
            }
            
            displayGroups[displayName].categoryValues.add(project.projet.typologie);
            displayGroups[displayName].projects.push(project);
        });
        
        // Convert to array and sort by order
        return Object.values(displayGroups)
            .sort((a, b) => a.order - b.order);
    };

    // Get label for display group
    const getDisplayGroupLabel = (displayGroup) => {
        // For merged categories, use the custom display name
        if (displayGroup.categoryValues.size > 1) {
            return displayGroup.displayName;
        }
        
        // For single categories, use the original translation
        const categoryValue = Array.from(displayGroup.categoryValues)[0];
        const category = FDCategories.find(cat => cat.value === categoryValue);
        return category ? category.label : displayGroup.displayName;
    };

    // Calculate statistics
    const totalProjects = ballotageProjects.length;
    const selectedProjects = ballotageProjects.filter(project => 
        userRatings.find(rating => 
            rating.participationId === project.projet.participationId && 
            rating.projetId === project.projet._id && 
            rating.jury2025_departage_point === "1"
        )
    ).length;
    const progressPercentage = totalProjects > 0 ? Math.round((selectedProjects / 4) * 100) : 0;

    if (loading) {
        return <Spinner />;
    }

    return (
        <div>
            <H2>Le FD100 - 4ème édition</H2>
            <span>Please select exactly 4 projects from the tiebreaker list below</span><br/><br/>

{/*             
            <InfoBox>
                <h3>Instructions:</h3>
                <ul>
                    <li>Please select exactly <strong>4 projects</strong> from the tiebreaker list below</li>
                    <li>These projects all have 13 votes, no "coup de cœur", and French fabrication</li>
                    <li>Your selections will help determine the final FD100 ranking</li>
                    <li>Changes are saved automatically</li>
                </ul>
            </InfoBox> */}
            
            <StatsContainer>
                <div className="stats-item">
                    <div className="number">{totalProjects}</div>
                    <div className="label">Tiebreaker projects</div>
                </div>
                
                <div className="progress-container">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{width: `${Math.min(progressPercentage, 100)}%`}}>
                            {progressPercentage > 15 && `${Math.min(progressPercentage, 100)}%`}
                        </div>
                    </div>
                    <div className="progress-label">
                        {selectedProjects} of 4 projects selected
                        {selectedProjects > 4 && <span style={{color: '#dc3545', fontWeight: 'bold'}}> (Too many selected!)</span>}
                    </div>
                </div>
                
                <div className="stats-item">
                    <div className="number">{selectedProjects}</div>
                    <div className="label">Selected</div>
                </div>
            </StatsContainer>

            {getDisplayGroups().map((group, i) => 
                <div key={i}>
                    <CategoryHeader className={`${group.type}-category`}>
                        <span style={{textTransform: 'uppercase', fontSize: '11px', fontWeight: 'bold', opacity: 0.9}}>
                            {group.type === 'objet' ? "OBJECT" : "SPACE"}
                        </span>
                        <span style={{marginLeft: '12px', fontSize: '14px'}}>
                            Category {i + 1}_{getDisplayGroupLabel(group)}
                        </span>
                    </CategoryHeader>
                    {group.projects.sort((a, b) => {
                        const displayNameA = getEventDesignerDisplayName(a.projet, a.user).toLowerCase();
                        const displayNameB = getEventDesignerDisplayName(b.projet, b.user).toLowerCase();
                        return displayNameA.localeCompare(displayNameB);
                    }).map((project, j) => {
                        const projectRating = userRatings.find(rating => 
                            rating.participationId === project.projet.participationId && 
                            rating.projetId === project.projet._id
                        );
                        const isSelected = projectRating?.jury2025_departage_point === "1";
                        
                        return <RatingLine key={j} className={isSelected ? 'selected' : ''}>
                            <div className='projinfo'>
                                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                                    <div>
                                        <div style={{display:"flex", alignItems:"center"}}>
                                            <a style={{fontWeight:"100"}} 
                                               href={"/admin/events/fdficheprojet?email="+project.user.user.email+"&pid="+project.user.participation._id+"&projid="+project.projet._id+"&mode=jury"} 
                                               target="_blank" 
                                               rel="noreferrer">
                                                Project in competition: <BoldBlue style={{textTransform: "capitalize", fontSize: "14px"}}>{(i18n.language !== 'fr' && project.projet.name_en ? project.projet.name_en : project.projet.name).toLowerCase()}</BoldBlue>
                                            </a>
                                            {project.projet.fabrication_fr === "oui" && <FabFrIndicator/>}
                                        </div>
                                        <a href={getEventDesignerProfileLink(project.projet, project.user)} 
                                           target="_blank" 
                                           rel="noreferrer">
                                            <span>Studio: {getEventDesignerDisplayName(project.projet, project.user)}</span>
                                        </a><br/>
                                    </div>
                                </div>
                            </div>
                            <label>
                                <span>Coup de cœur for tiebreaker:</span>
                                <input 
                                    onChange={(e) => setDepartagePoint(project, e)} 
                                    type="checkbox" 
                                    checked={isSelected}
                                    disabled={!isSelected && selectedProjects >= 4}
                                />
                            </label>
                        </RatingLine>
                    })}
                </div>
            )}
            
            <SaveNotification status={dataStatus} show={showSaveNotification}>
                {dataStatus === "loading" && (
                    <>
                        <div className="spinner"></div>
                        Saving...
                    </>
                )}
                {dataStatus === "saved" && (
                    <>
                        <span className="checkmark">✓</span>
                        Auto-save successful
                    </>
                )}
            </SaveNotification>
        </div>
    );
};

export default FD100_2025JuryDepartage;